import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/format';
import type { AddonAvailability, AddonSelection } from '@/api/hooks/useAddons';

type AddonSelectorProps = {
  addons: AddonAvailability[];
  selections: AddonSelection[];
  onChange: (selections: AddonSelection[]) => void;
  currency: string;
};

export default function AddonSelector({
  addons,
  selections,
  onChange,
  currency,
}: AddonSelectorProps) {
  const { t, i18n } = useTranslation();

  const getQuantity = (addonId: string) =>
    selections.find((s) => s.addonId === addonId)?.quantity || 0;

  const updateQuantity = (addonId: string, delta: number) => {
    const item = addons.find((e) => e.id === addonId);
    if (!item) return;
    const currentQty = getQuantity(addonId);
    const newQty = Math.max(0, Math.min(item.availableQuantity, currentQty + delta));
    const updated = selections.filter((s) => s.addonId !== addonId);
    if (newQty > 0) {
      updated.push({ addonId, quantity: newQty });
    }
    onChange(updated);
  };

  const totalCost = selections.reduce((sum, sel) => {
    const item = addons.find((e) => e.id === sel.addonId);
    return sum + (item ? item.rentalPrice * sel.quantity : 0);
  }, 0);

  if (addons.length === 0) return null;

  return (
    <div className="space-y-2 border-t pt-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('addons_customer.add_addons')}
      </p>

      {addons.map((item) => {
        const qty = getQuantity(item.id);
        return (
          <div key={item.id} className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{item.name}</p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(item.rentalPrice, currency, i18n.language)} · {item.availableQuantity}{' '}
                {t('addons_customer.available')}
              </p>
            </div>
            <div className="ml-3 flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); updateQuantity(item.id, -1); }}
                disabled={qty === 0}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="w-6 text-center text-sm font-medium">{qty}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); updateQuantity(item.id, 1); }}
                disabled={qty >= item.availableQuantity}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        );
      })}

      {totalCost > 0 && (
        <div className="flex justify-between border-t pt-1 text-sm">
          <span className="text-muted-foreground">{t('addons_customer.addon_total')}</span>
          <span className="font-mono font-medium">+{formatCurrency(totalCost, currency, i18n.language)}</span>
        </div>
      )}
    </div>
  );
}
